import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import getStatusForOutputFileButton from 'lab-automation/automation-files/getStatusForOutputFileButton';
import {RunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import {ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';

const AutomationFileUtil = {
  generateAutomationOutputFilesByName(
    automationOutputFiles: Array<AutomationOutputFileGraphQL>
  ): [Record<string, Array<AutomationOutputFileGraphQL>>, Array<AutomationOutputFileGraphQL>] {
    const automationOutputFilesByName: Record<string, Array<AutomationOutputFileGraphQL>> = {};
    for (const automationOutputFile of automationOutputFiles) {
      const configName = automationOutputFile.automationOutputFileConfig.name;
      if (automationOutputFilesByName[configName]) {
        automationOutputFilesByName[configName].push(automationOutputFile);
      } else {
        automationOutputFilesByName[configName] = [automationOutputFile];
      }
    }
    return [automationOutputFilesByName, automationOutputFiles];
  },
  generateAutomationOutputFileIdsToStructuredTable(
    versionedStructuredTables?: Array<RunOutputSamplesTable | ResultsTable>
  ): {
    [id: string]: RunOutputSamplesTable | ResultsTable;
  } {
    const automationOutputFileIdsToStructuredTable: {[id: string]: RunOutputSamplesTable | ResultsTable} = {};
    const structuredTables = versionedStructuredTables || [];
    for (const versionedStructuredTable of structuredTables) {
      if (versionedStructuredTable.metadata.automationOutputFileId) {
        automationOutputFileIdsToStructuredTable[versionedStructuredTable.metadata.automationOutputFileId] =
          versionedStructuredTable;
      }
    }
    return automationOutputFileIdsToStructuredTable;
  },
  generateConfigNameToLastSucceededAutomationOutputFiles(
    automationOutputFilesByName: Record<string, Array<AutomationOutputFileGraphQL>>
  ): Record<string, AutomationOutputFileGraphQL | null> {
    return Object.entries(automationOutputFilesByName).reduce(
      (accObj: Record<string, AutomationOutputFileGraphQL | null>, [prevConfigName, prevAofs]) => {
        const activeAutomationOutputFileIdx = prevAofs
          .reverse()
          .findIndex((aof) => aof.status === AutomationFileStatusGraphQL.succeeded);
        const activeAutomationOutputFile =
          activeAutomationOutputFileIdx >= 0 ? prevAofs[activeAutomationOutputFileIdx] : null;
        accObj[prevConfigName] = activeAutomationOutputFile;
        return accObj;
      },
      {}
    );
  },
  filterAutomationOutputFiles(
    automationOutputFiles: Array<AutomationOutputFileGraphQL>
  ): Array<AutomationOutputFileGraphQL> | null {
    if (!automationOutputFiles?.length) {
      return null;
    }

    const filteredAutomationOutputFiles = automationOutputFiles.filter((automationOutputFile) => {
      const isHidden = automationOutputFile.automationOutputFileConfig.isHidden;

      if (isHidden) {
        // We only want to show the ones that have a failed status if they're otherwise hidden
        return getStatusForOutputFileButton(automationOutputFile) === AutomationFileStatusGraphQL.failed;
      } else {
        // Since the files aren't hidden, show any that have a blob
        return automationOutputFile.blob != null;
      }
    });

    // if there are no errored files, we show nothing
    return filteredAutomationOutputFiles.length > 0 ? filteredAutomationOutputFiles : null;
  },
  getAutomationOutputStructuredTables(
    assayData: NoteLinkedAssayData,
    {addRunSchemaName}: {addRunSchemaName?: boolean} = {}
  ): Array<RunOutputSamplesTable | ResultsTable> {
    if (assayData.type !== NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA) {
      return [];
    }
    const versionedData = assayData.versionedData;
    const automationOutputFileIdsToStructuredTable =
      AutomationFileUtil.generateAutomationOutputFileIdsToStructuredTable(versionedData?.structuredTables);
    const automationOutputFileIds =
      AutomationFileUtil.filterAutomationOutputFiles(versionedData?.automationOutputFiles ?? [])?.map(
        (file) => file.id
      ) ?? [];
    const structuredTables = automationOutputFileIds
      .map((id) => automationOutputFileIdsToStructuredTable[id])
      .filter((table) => table != null);

    if (addRunSchemaName) {
      return structuredTables.map((table) => ({
        ...table,
        name: `${versionedData?.runSchemaName} - ${table.name}`,
      }));
    }

    return structuredTables;
  },
};
export default AutomationFileUtil;
